import { useCallback, useMemo } from "react";
import { trpc } from "../trpc";

const EMPTY: never[] = [];
const DOMAIN_FILTER = "domain_filter";
const STALE_TIME = 1000 * 60 * 5; // 5 minutes

export interface DomainRestriction {
  mode: string;
  domains: string[];
}

export function useQueryDomainFilterGuards() {
  const guardsQuery = trpc.guard.getGuards.useQuery(
    { guardType: "domain_filter", isActive: true },
    { staleTime: STALE_TIME },
  );
  const typesQuery = trpc.guard.getGuardTypes.useQuery(undefined, {
    staleTime: STALE_TIME,
  });

  const guards = guardsQuery.data ?? EMPTY;

  // `compatibleApis` for domain_filter: a string[] once guard types load,
  // otherwise undefined (types not loaded, or the backend returned none).
  // While undefined, an "all" guard matches nothing — we don't know its
  // compatible set yet, so we don't over-badge.
  const compatibleApis = useMemo(() => {
    const type = (typesQuery.data ?? []).find(
      (t) => t.guardType === DOMAIN_FILTER,
    );
    return type?.compatibleApis;
  }, [typesQuery.data]);

  const getDomainRestriction = useCallback(
    (appId: string, operation: string): DomainRestriction | undefined => {
      // Guard selectedApis are versionless (e.g. "GoogleMailV2CLIAPI").
      const strippedId = appId?.split("@")[0] ?? "";
      if (!strippedId) return undefined;

      for (const guard of guards) {
        if (!guard.operations?.includes(operation)) continue;

        const appliesToApp =
          guard.selectedApis === "all"
            ? (compatibleApis?.includes(strippedId) ?? false)
            : guard.selectedApis.includes(strippedId);

        if (!appliesToApp) continue;

        // First match wins; skip a guard missing mode/domains (would render empty).
        if (!guard.mode || !guard.domains?.length) continue;
        return { mode: guard.mode, domains: guard.domains };
      }
      return undefined;
    },
    [guards, compatibleApis],
  );

  return {
    getDomainRestriction,
    isLoading: guardsQuery.isLoading || typesQuery.isLoading,
    isError: guardsQuery.isError || typesQuery.isError,
  };
}
